import {useMemo, useRef, useState} from 'react';
import {GetFormattedBulkPointModifiers} from '@/models/BulkModifierModel';
import {useDataTable} from '@/hooks/useDataTable-hook';
import {buildQueryParams} from '@/utils/commons';
import {
  getBulkModifierCsvFile,
  getBulkModifiers
} from '@/services/bulkModifiers';
import {BulkModifiersListColConfig} from '@/services/MockData/BulkModifiersList/BulkModifiersListColConfig';
import {useQueries, keepPreviousData} from '@tanstack/react-query';
import {downloadCSVFiles, getDateWithTimeString} from '@/helpers/shadcn/utils';
import {logger} from '@/helpers/logger';

const initialSortConfig = {
  column: BulkModifiersListColConfig[2].name_of_db_column,
  direction: 'desc'
};

export const useBulkModifiers = () => {
  const [pageSize, setPageSize] = useState(10);

  const {
    setPageIndex,
    setSortingObj,
    setSearchArry,
    pageIndex,
    sortingObj,
    searchArr,
    handleResetAll
  } = useDataTable(0, false, false, initialSortConfig);

  // Move search params to request body and only use query params for pagination/sorting
  const queryParams = useMemo(() => {
    return buildQueryParams({
      ...(sortingObj?.column && {
        sortBy: sortingObj.column,
        order: sortingObj.direction
      }),
      pageSize,
      page: pageIndex || 0
    });
  }, [sortingObj?.column, sortingObj?.direction, pageIndex, pageSize]);

  const [{data, isLoading, isFetching}] = useQueries({
    queries: [
      {
        queryKey: ['BULK_MODIFIERS', queryParams, searchArr],
        queryFn: () => getBulkModifiers(queryParams, searchArr),
        placeholderData: keepPreviousData
      }
    ]
  });

  const resultsData = useMemo(() => data || {}, [data]);
  const parsedData = useMemo(
    () => GetFormattedBulkPointModifiers(resultsData),
    [resultsData]
  );

  // Rapid page/filter changes can break the keepPreviousData chain, leaving
  // `data` briefly undefined mid-fetch. Serve the last good payload during
  // that gap; settled results always pass through.
  const lastGoodDataRef = useRef(null);
  const bulkModifiersData = useMemo(() => {
    const hasRows = parsedData?.data?.length > 0;
    if (hasRows || !isFetching) {
      lastGoodDataRef.current = parsedData;
      return parsedData;
    }
    return lastGoodDataRef.current ?? parsedData;
  }, [parsedData, isFetching]);

  // Some responses omit totalRecords on later pages — hold the last known
  // total while rows exist; a genuinely empty result resets it.
  const lastKnownTotalRef = useRef(0);
  const totalRecords = useMemo(() => {
    const parsed = Number(bulkModifiersData?.totalRecords);
    if (Number.isFinite(parsed) && parsed > 0) {
      lastKnownTotalRef.current = parsed;
      return parsed;
    }
    return bulkModifiersData?.data?.length > 0 ? lastKnownTotalRef.current : 0;
  }, [bulkModifiersData]);

  const onDownloadBtnClick = async (selectedIds, excludeIds = []) => {
    const requestBody = {
      ids: selectedIds,
      excludeIds,
      filters: searchArr
    };

    try {
      const data = await getBulkModifierCsvFile(queryParams, requestBody);
      const fileName = `BulkModifier_${getDateWithTimeString()}`;
      downloadCSVFiles(data, fileName);
    } catch (error) {
      logger.error('Error downloading CSV file:', error);
    }
  };

  return {
    bulkModifiersData,
    totalRecords,
    bulkModifiersColConfigData: BulkModifiersListColConfig,
    isLoading,
    isFetching,
    pageSize,
    setPageSize,
    setPageIndex,
    setSortingObj,
    setSearchArry,
    pageIndex,
    sortingObj,
    searchArr,
    handleResetAll,
    onDownloadBtnClick
  };
};
